import { Check, Loader2, RotateCcw, X, AlertTriangle } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { Multiplier, resetAllMultipliers } from "@/api/multipliers";

interface SettingsActionsProps {
    isLoading: boolean;
    multipliers: Multiplier[];
    editedMultipliers: Map<number, Partial<Multiplier>>;
    setEditedMultipliers: (map: Map<number, Partial<Multiplier>>) => void;
}

export function SettingsActions({ isLoading, setEditedMultipliers }: SettingsActionsProps) {
    const [showResetModal, setShowResetModal] = useState(false);
    const queryClient = useQueryClient();

    const resetAllMutation = useMutation({
        mutationFn: resetAllMultipliers,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["multipliers"] });
            setEditedMultipliers(new Map());
            setShowResetModal(false);
            toast.success("All multipliers reset to default!", {
                icon: <Check className="w-4 h-4 text-general-green" />,
                description: "To apply the changes, recalculate the database",
            });
        },
        onError: (error: Error) => {
            toast.error(error.message || "Failed to reset all multipliers", {
                icon: <X className="w-4 h-4 text-general-red" />,
            });
        },
    });

    const handleResetClick = () => {
        setShowResetModal(true);
    };

    const handleConfirmReset = () => {
        resetAllMutation.mutate();
    };

    const handleCancelReset = () => {
        setShowResetModal(false);
    };

    return (
        <>
            <div className="flex justify-end pb-0 pt-3.5 mt-4">
                <button
                    onClick={handleResetClick}
                    disabled={isLoading || resetAllMutation.isPending}
                    className="w-full sm:w-auto justify-center px-4 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer disabled:bg-surface-container-high disabled:text-on-surface-variant disabled:cursor-not-allowed text-white"
                    style={{ background: "#ba0a06" }}
                    id="onboarding-settings-reset-all"
                >
                    <RotateCcw className="w-4 h-4" />
                    Reset All to Default
                </button>
            </div>

            {showResetModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-surface-container-lowest border border-outline-variant/20 rounded-md shadow-[0_30px_60px_rgba(5,0,102,0.06)] w-full max-w-md mx-4 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-outline-variant/20 flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="w-6 h-6 text-general-red" />
                                <h2 className="text-xl font-headline font-bold text-on-surface">
                                    Reset All Multipliers
                                </h2>
                            </div>
                            <button
                                onClick={handleCancelReset}
                                className="p-2 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors cursor-pointer"
                                disabled={resetAllMutation.isPending}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="space-y-4">
                                <p className="text-on-surface-variant text-sm leading-relaxed">
                                    Are you sure you want to reset all multipliers to their default values? This action
                                    will:
                                </p>
                                <div className="bg-surface-container rounded-md p-4 space-y-2">
                                    <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                                        <div className="w-1.5 h-1.5 bg-general-red rounded-full"></div>
                                        <span>Restore all weight values to defaults</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                                        <div className="w-1.5 h-1.5 bg-general-red rounded-full"></div>
                                        <span>Restore all max points to defaults</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                                        <div className="w-1.5 h-1.5 bg-general-red rounded-full"></div>
                                        <span>Discard any unsaved changes</span>
                                    </div>
                                </div>
                                <div className="bg-general-red-background border border-general-red/30 rounded-md p-3">
                                    <p className="text-general-red text-sm font-medium">
                                        ⚠️ This action cannot be undone
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-low flex-shrink-0">
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={handleCancelReset}
                                    disabled={resetAllMutation.isPending}
                                    className="px-4 py-2 text-on-surface-variant hover:text-on-surface border border-outline-variant hover:border-outline rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmReset}
                                    disabled={resetAllMutation.isPending}
                                    className="bg-general-red text-white px-4 py-2 rounded-md font-medium hover:brightness-110 transition-all border border-general-red flex items-center gap-2 cursor-pointer disabled:bg-surface-container-high disabled:text-on-surface-variant disabled:cursor-not-allowed disabled:border-outline-variant"
                                >
                                    {resetAllMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        <>
                                            <RotateCcw className="w-4 h-4" />
                                            Reset All
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
